import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    Anchor,
    Flame,
    Wheat,
    Layers,
    FlaskConical,
    Users,
    Link2,
    FileSignature,
    Handshake,
    Network,
    Globe2,
    Banknote,
    ShieldCheck,
    Send,
    Loader2,
    Phone,
    Mail,
    MapPin,
} from "lucide-react";
import { toast } from "sonner";
import GlobeLines from "@/components/visual/GlobeLines";
import { FadeUp, Overline, SectionHeading } from "@/components/visual/SectionHeading";
import SEO from "@/components/seo/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const commodities = [
    { icon: Flame, title: "Energy Products", desc: "Crude oil, refined fuels (EN590, Jet A1), and related energy commodities." },
    { icon: Wheat, title: "Agricultural Inputs", desc: "Urea, NPK, fertilizers, and agri-commodities." },
    { icon: Layers, title: "Metals & Minerals", desc: "Industrial metals, raw materials, and bulk commodities." },
    { icon: FlaskConical, title: "Industrial & Specialty", desc: "Chemicals, lubricants, and specialised materials." },
];

const services = [
    "Import-export structuring and advisory",
    "Commodities transaction support (advisory role)",
    "Supplier and buyer coordination",
    "Supply chain and logistics advisory",
    "Commercial contract structuring (SPA, FCO, LOI)",
    "Cross-border transaction facilitation",
];

const executionPoints = [
    { icon: Users, title: "Representation & Market Access" },
    { icon: Link2, title: "Deal Structuring & Execution Support" },
    { icon: FileSignature, title: "Documentation & Negotiation Advisory" },
    { icon: Handshake, title: "Strategic Partnerships & Introductions" },
    { icon: Network, title: "Counterparty, Bank & Logistics Coordination" },
];

const flow = [
    { n: "01", t: "Opportunity Assessment", d: "Validation of opportunity, counterparties, and viability." },
    { n: "02", t: "Counterparty Alignment", d: "Buyer / supplier mandate confirmation." },
    { n: "03", t: "Commercial Structuring", d: "Pricing framework and commercial architecture." },
    { n: "04", t: "Financial Instruments", d: "Alignment of LC, SBLC, BG and payment security." },
    { n: "05", t: "Documentation", d: "Contract, SPA, FCO, LOI negotiation." },
    { n: "06", t: "Execution & Delivery", d: "Closing, logistics, and delivery coordination." },
];

const tradeFinance = [
    "Letters of Credit (LC)",
    "Standby Letters of Credit (SBLC)",
    "Bank Guarantees (BG)",
    "Payment security structures",
    "Multi-bank coordination",
];

const regions = ["Asia", "Middle East", "Europe", "Africa"];

const criteria = [
    "Clear buyer/supplier mandate",
    "Financial capability (proof of funds / LC readiness)",
    "Defined transaction scope and volume",
    "Compliance readiness (KYC / AML)",
];

const why = [
    "Execution-focused advisory",
    "Multi-sector expertise (trade, energy, finance)",
    "Global network across suppliers and institutions",
    "Structured, compliance-driven approach",
    "Confidential engagement model",
];

const initialForm = {
    name: "",
    company: "",
    email: "",
    phone: "",
    role: "buyer",
    commodity: "",
    quantity: "",
    destination: "",
    payment_method: "LC",
    documents: "",
    notes: "",
};

const Trade = () => {
    const [form, setForm] = useState(initialForm);
    const [submitting, setSubmitting] = useState(false);

    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.commodity.trim()) {
            toast.error("Name, email, and commodity are required.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/trade-opportunity`, form);
            toast.success("Trade opportunity received. A principal will respond within two working days.");
            setForm(initialForm);
        } catch (err) {
            const detail = err?.response?.data?.detail || "Unable to submit at this time. Please try again.";
            toast.error(typeof detail === "string" ? detail : "Submission failed.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="page-enter">
            <SEO
                title="International Trade & Commodities Advisory"
                description="Structured advisory and execution support for cross-border trade, supply chains, and commodity transactions. Energy, agri, metals, specialty products."
                path="/trade"
                keywords="international trade advisory, commodities advisory, EN590, Jet A1, urea NPK, SBLC LC BG, import export structuring, supply chain advisory, trade finance"
            />

            {/* HERO */}
            <section className="relative min-h-[80vh] flex items-center overflow-hidden" data-testid="trade-hero">
                <img
                    src="https://images.unsplash.com/photo-1605745341112-85968b19335b?auto=format&fit=crop&w=2400&q=80"
                    alt="Container port and global trade"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-[#040B1A]/85" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#040B1A] via-[#040B1A]/80 to-[#040B1A]/40" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#040B1A]" />
                <GlobeLines className="opacity-[0.12]" />
                <div className="grain-overlay" />

                <div className="max-w-7xl mx-auto px-6 md:px-10 py-28 relative z-10 w-full">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="max-w-4xl"
                    >
                        <div className="inline-flex items-center gap-3 mb-10">
                            <span className="h-px w-10 bg-[#C9A14A]" />
                            <span className="overline">Trade & Commodities</span>
                        </div>
                        <h1 className="font-serif text-5xl md:text-7xl lg:text-[5.25rem] text-white leading-[1.0] tracking-tight">
                            International Trade &<br />
                            <span className="italic font-light text-[#C9A14A]">Commodities Advisory.</span>
                        </h1>
                        <p className="mt-10 text-lg md:text-xl text-slate-300 font-light leading-[1.8] max-w-2xl">
                            Structured advisory and execution support for cross-border trade, supply chains, and
                            commodity transactions.
                        </p>
                        <p className="mt-3 text-[0.7rem] tracking-[0.3em] uppercase text-[#C9A14A]">
                            Precision · Power · Partnerships
                        </p>
                        <div className="mt-12 flex flex-col sm:flex-row gap-5">
                            <a href="#submit" className="btn-primary group" data-testid="trade-hero-submit">
                                Submit Trade Opportunity
                                <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                            </a>
                            <Link to="/contact" className="btn-secondary" data-testid="trade-hero-consultation">
                                Request Consultation
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* OVERVIEW */}
            <section className="py-24 md:py-32 border-t border-[#C9A14A]/15" data-testid="trade-overview">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
                    <FadeUp className="lg:col-span-4">
                        <Overline>Overview</Overline>
                        <h2 className="font-serif text-4xl md:text-5xl text-white mt-6 leading-[1.05]">
                            Cross-border trade, executed through <em className="text-[#C9A14A] not-italic">structured advisory.</em>
                        </h2>
                    </FadeUp>
                    <FadeUp delay={0.15} className="lg:col-span-8 space-y-6 text-lg text-slate-300 font-light leading-[1.9]">
                        <p>
                            Indravax Strategic Advisory LLP provides advisory and facilitation services across
                            international trade and commodities, supporting clients in
                            <span className="text-[#C9A14A]"> structuring and executing cross-border commercial
                            transactions.</span>
                        </p>
                        <p>
                            The firm operates across multiple jurisdictions, enabling alignment between
                            suppliers, buyers, logistics partners, and financial institutions to ensure
                            efficient and compliant trade execution.
                        </p>
                    </FadeUp>
                </div>
            </section>

            {/* COMMODITIES */}
            <section className="py-24 md:py-32 bg-[#07132E]/40" data-testid="trade-commodities">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <SectionHeading
                        overline="Commodities & Trade Segments"
                        title="Four commodity verticals."
                        description="Indravax engages selectively across commodity segments where our advisory and counterparty network adds material value."
                        testId="trade-commodities-heading"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {commodities.map((c, i) => {
                            const Icon = c.icon;
                            return (
                                <FadeUp key={c.title} delay={i * 0.08}>
                                    <div className="institutional-card h-full" data-testid={`commodity-${i}`}>
                                        <div className="flex items-start justify-between">
                                            <Icon size={28} strokeWidth={1.2} className="text-[#C9A14A]" />
                                            <span className="text-[0.65rem] tracking-[0.3em] text-slate-500 uppercase">0{i + 1}</span>
                                        </div>
                                        <h3 className="font-serif text-2xl text-white mt-8 leading-tight">{c.title}</h3>
                                        <p className="text-sm text-slate-400 mt-4 font-light leading-relaxed">{c.desc}</p>
                                    </div>
                                </FadeUp>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* SERVICES */}
            <section className="py-24 md:py-32" data-testid="trade-services">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <FadeUp className="lg:col-span-5">
                        <Overline>Trade & Commercial Advisory</Overline>
                        <h2 className="font-serif text-4xl md:text-5xl text-white mt-6 leading-[1.05]">
                            Six services. <span className="italic font-light text-[#C9A14A]">One coordinated framework.</span>
                        </h2>
                    </FadeUp>
                    <FadeUp delay={0.15} className="lg:col-span-7">
                        <ul className="border-t border-[#C9A14A]/20">
                            {services.map((s, i) => (
                                <li key={s} className="flex items-baseline gap-6 py-5 border-b border-[#C9A14A]/15">
                                    <span className="text-[0.65rem] tracking-[0.3em] text-[#C9A14A] w-8 shrink-0">0{i + 1}</span>
                                    <span className="text-base md:text-lg text-slate-200 font-light leading-relaxed">{s}</span>
                                </li>
                            ))}
                        </ul>
                    </FadeUp>
                </div>
            </section>

            {/* EXECUTION CAPABILITIES */}
            <section className="py-24 md:py-32 bg-[#07132E]/40 relative overflow-hidden" data-testid="trade-execution">
                <GlobeLines className="opacity-[0.06]" />
                <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
                    <SectionHeading
                        overline="Execution & Facilitation"
                        title="Trade transactions, supported end-to-end."
                        testId="trade-execution-heading"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                        {executionPoints.map((e, i) => {
                            const Icon = e.icon;
                            return (
                                <FadeUp key={e.title} delay={i * 0.06}>
                                    <div className="institutional-card h-full" data-testid={`trade-exec-${i}`}>
                                        <Icon size={26} strokeWidth={1.1} className="text-[#C9A14A]" />
                                        <h3 className="font-serif text-lg text-white mt-6 leading-tight">{e.title}</h3>
                                    </div>
                                </FadeUp>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* TRANSACTION FLOW */}
            <section className="py-24 md:py-32" data-testid="trade-flow">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <SectionHeading
                        overline="Typical Transaction Flow"
                        title="A six-stage execution sequence."
                        testId="trade-flow-heading"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-[#C9A14A]/20">
                        {flow.map((s, i) => (
                            <FadeUp
                                key={s.n}
                                delay={i * 0.06}
                                className={`p-8 md:p-10 border-b border-[#C9A14A]/20 ${(i + 1) % 3 !== 0 ? "lg:border-r" : ""} ${(i + 1) % 2 !== 0 ? "md:border-r lg:border-r" : ""}`}
                            >
                                <div className="font-serif text-5xl text-[#C9A14A] italic">{s.n}</div>
                                <h3 className="font-serif text-2xl text-white mt-6 leading-tight">{s.t}</h3>
                                <p className="text-sm text-slate-400 mt-3 font-light leading-relaxed">{s.d}</p>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* TRADE FINANCE + GLOBAL */}
            <section className="py-24 md:py-32 bg-[#07132E]/40" data-testid="trade-finance-global">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-10">
                    <FadeUp>
                        <div className="institutional-card h-full">
                            <Banknote size={32} strokeWidth={1.1} className="text-[#C9A14A]" />
                            <Overline className="mt-6 block">Trade Finance & Payment Structuring</Overline>
                            <h3 className="font-serif text-3xl text-white mt-4 leading-tight">
                                Financial frameworks supporting trade.
                            </h3>
                            <ul className="mt-8 space-y-4">
                                {tradeFinance.map((t) => (
                                    <li key={t} className="flex items-start gap-4 pb-4 border-b border-[#C9A14A]/10 last:border-0">
                                        <span className="h-px w-4 bg-[#C9A14A] mt-3 shrink-0" />
                                        <span className="text-slate-300 font-light">{t}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </FadeUp>
                    <FadeUp delay={0.1}>
                        <div className="institutional-card h-full">
                            <Globe2 size={32} strokeWidth={1.1} className="text-[#C9A14A]" />
                            <Overline className="mt-6 block">Global Trade Network</Overline>
                            <h3 className="font-serif text-3xl text-white mt-4 leading-tight">
                                Operating across four corridors.
                            </h3>
                            <p className="mt-6 text-slate-300 font-light leading-[1.9]">
                                Indravax enables cross-border trade by aligning regional expertise with global
                                market access.
                            </p>
                            <div className="mt-8 grid grid-cols-2 gap-3">
                                {regions.map((r, i) => (
                                    <div key={r} className="flex items-center gap-4 py-3 border-b border-[#C9A14A]/15">
                                        <span className="text-[0.65rem] tracking-[0.3em] text-[#C9A14A] w-6">0{i + 1}</span>
                                        <span className="font-serif text-xl text-white">{r}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* CRITERIA + WHY */}
            <section className="py-24 md:py-32" data-testid="trade-criteria-why">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-10">
                    <FadeUp>
                        <div className="institutional-card h-full">
                            <ShieldCheck size={32} strokeWidth={1.1} className="text-[#C9A14A]" />
                            <Overline className="mt-6 block">Client & Transaction Requirements</Overline>
                            <h3 className="font-serif text-3xl text-white mt-4 leading-tight">
                                We engage with verified counterparties and structured opportunities.
                            </h3>
                            <ul className="mt-8 space-y-4">
                                {criteria.map((c) => (
                                    <li key={c} className="flex items-start gap-4 pb-4 border-b border-[#C9A14A]/10 last:border-0">
                                        <span className="h-px w-4 bg-[#C9A14A] mt-3 shrink-0" />
                                        <span className="text-slate-300 font-light">{c}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </FadeUp>
                    <FadeUp delay={0.1}>
                        <div className="institutional-card h-full">
                            <Anchor size={32} strokeWidth={1.1} className="text-[#C9A14A]" />
                            <Overline className="mt-6 block">Why Indravax</Overline>
                            <h3 className="font-serif text-3xl text-white mt-4 leading-tight">
                                Built for execution, not noise.
                            </h3>
                            <ul className="mt-8 space-y-4">
                                {why.map((w) => (
                                    <li key={w} className="flex items-start gap-4 pb-4 border-b border-[#C9A14A]/10 last:border-0">
                                        <span className="h-px w-4 bg-[#C9A14A] mt-3 shrink-0" />
                                        <span className="text-slate-300 font-light">{w}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* SUBMIT TRADE OPPORTUNITY FORM */}
            <section id="submit" className="py-24 md:py-32 bg-[#07132E]/40 scroll-mt-28" data-testid="trade-submit">
                <div className="max-w-5xl mx-auto px-6 md:px-10">
                    <SectionHeading
                        align="center"
                        overline="Initiate a Trade Engagement"
                        title="Submit a trade opportunity for confidential review."
                        description="Submissions are reviewed by a principal. We respond within two working days under strict confidentiality."
                        testId="trade-submit-heading"
                    />
                    <FadeUp>
                        <form onSubmit={onSubmit} className="institutional-card p-8 md:p-12" data-testid="trade-form">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
                                <div className="md:col-span-1">
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Name *</label>
                                    <input type="text" name="name" value={form.name} onChange={onChange} className="institutional-input" placeholder="Full name" required data-testid="trade-form-name" />
                                </div>
                                <div className="md:col-span-1">
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Company</label>
                                    <input type="text" name="company" value={form.company} onChange={onChange} className="institutional-input" placeholder="Organisation" data-testid="trade-form-company" />
                                </div>
                                <div className="md:col-span-1">
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Email *</label>
                                    <input type="email" name="email" value={form.email} onChange={onChange} className="institutional-input" placeholder="you@org.com" required data-testid="trade-form-email" />
                                </div>
                                <div className="md:col-span-1">
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Phone</label>
                                    <input type="tel" name="phone" value={form.phone} onChange={onChange} className="institutional-input" placeholder="+..." data-testid="trade-form-phone" />
                                </div>

                                <div className="md:col-span-1">
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Role</label>
                                    <select name="role" value={form.role} onChange={onChange} className="institutional-input bg-[#040B1A]" data-testid="trade-form-role">
                                        <option value="buyer">Buyer</option>
                                        <option value="seller">Seller</option>
                                        <option value="both">Both / Intermediary</option>
                                    </select>
                                </div>
                                <div className="md:col-span-1">
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Commodity *</label>
                                    <input type="text" name="commodity" value={form.commodity} onChange={onChange} className="institutional-input" placeholder="e.g. EN590, Urea, Copper Cathodes" required data-testid="trade-form-commodity" />
                                </div>
                                <div className="md:col-span-1">
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Quantity</label>
                                    <input type="text" name="quantity" value={form.quantity} onChange={onChange} className="institutional-input" placeholder="e.g. 50,000 MT / 2,000,000 BBL" data-testid="trade-form-quantity" />
                                </div>
                                <div className="md:col-span-1">
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Destination</label>
                                    <input type="text" name="destination" value={form.destination} onChange={onChange} className="institutional-input" placeholder="Port / Country" data-testid="trade-form-destination" />
                                </div>

                                <div className="md:col-span-1">
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Payment Method</label>
                                    <select name="payment_method" value={form.payment_method} onChange={onChange} className="institutional-input bg-[#040B1A]" data-testid="trade-form-payment">
                                        <option value="LC">Letter of Credit (LC)</option>
                                        <option value="SBLC">Standby LC (SBLC)</option>
                                        <option value="BG">Bank Guarantee (BG)</option>
                                        <option value="TT">Telegraphic Transfer (TT)</option>
                                        <option value="Other">Other / To Discuss</option>
                                    </select>
                                </div>
                                <div className="md:col-span-1">
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Documents Available</label>
                                    <input type="text" name="documents" value={form.documents} onChange={onChange} className="institutional-input" placeholder="LOI, ICPO, FCO, SPA, POF…" data-testid="trade-form-documents" />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] mb-3 block">Additional Notes</label>
                                    <textarea name="notes" value={form.notes} onChange={onChange} className="institutional-input" placeholder="Specifications, timelines, structure, jurisdictions." rows={4} data-testid="trade-form-notes" />
                                </div>
                            </div>

                            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                                <p className="text-xs text-slate-500 max-w-md leading-relaxed font-light">
                                    By submitting, you confirm the enquiry is made in good faith and in compliance
                                    with applicable laws.
                                </p>
                                <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed" data-testid="trade-form-submit">
                                    {submitting ? (<><Loader2 size={14} className="animate-spin" /> Submitting</>) : (<>Submit Trade Opportunity <Send size={14} /></>)}
                                </button>
                            </div>
                        </form>
                    </FadeUp>
                </div>
            </section>

            {/* CONTACT + DISCLAIMER */}
            <section className="py-20 border-t border-[#C9A14A]/15" data-testid="trade-contact">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-3 gap-10">
                    <div className="flex gap-3 text-sm text-slate-400">
                        <MapPin size={16} className="text-[#C9A14A] mt-1 shrink-0" />
                        <span>Indravax Strategic Advisory LLP<br />New Delhi, India</span>
                    </div>
                    <div className="flex gap-3 text-sm text-slate-400">
                        <Phone size={16} className="text-[#C9A14A] mt-1 shrink-0" />
                        <a href="tel:+917814050036" className="hover:text-[#C9A14A] transition-colors">+91 78140 50036</a>
                    </div>
                    <div className="flex gap-3 text-sm text-slate-400">
                        <Mail size={16} className="text-[#C9A14A] mt-1 shrink-0" />
                        <a href="mailto:prateekgrewal@indravax.com" className="hover:text-[#C9A14A] transition-colors">prateekgrewal@indravax.com</a>
                    </div>
                </div>
                <div className="max-w-5xl mx-auto px-6 md:px-10 mt-12">
                    <p className="text-xs text-slate-500 font-light leading-relaxed border-l-2 border-[#C9A14A]/40 pl-5 italic">
                        Indravax Strategic Advisory LLP provides advisory and facilitation services in
                        international trade and does not act as a principal buyer or seller unless specifically
                        authorized under applicable laws and agreements.
                    </p>
                </div>
            </section>
        </div>
    );
};

export default Trade;
